import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, waitForProperty } from 'ember-concurrency';
import ENV from 'lumen/config/environment';
import type { LookerDashboard, LookerFolder } from 'lumen/models';

type DashboardOperationType = 'create' | 'edit' | 'delete' | 'copy' | 'move';
type FolderOperationType = 'create' | 'edit' | 'delete' | 'move';
// https://cloud.google.com/looker/docs/embedded-javascript-events#action_summary_table
type LookerActions =
  | 'dashboard:load'
  | 'dashboard:run'
  | 'dashboard:edit'
  | 'dashboard:filters:update'
  | 'dashboard:options:set'
  | 'dashboard:schedule_modal:open'
  | 'dashboard:stop'
  | 'look:run'
  | 'look:filters:update'
  | 'explore:run'
  | 'explore:filters:update'
  | 'session:tokens:request'
  | 'env:host:scroll';
type LookerRequest = {
  filters?: Record<string, unknown>;
  type: LookerActions;
};

interface FolderOperation {
  after: () => void;
  close: () => void;
  folder: LookerFolder;
  type: FolderOperationType;
}

interface DashboardOperation {
  after: (dashboard?: LookerDashboard) => void;
  close: () => void;
  dashboard: LookerDashboard;
  type: DashboardOperationType;
}

export default class CustomReportingService extends Service {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @tracked dashboardLoading = false;
  @tracked dashboardOperation?: DashboardOperation;
  @tracked folderOperation?: FolderOperation;
  @tracked pendingScheduleRequest = false;
  @tracked selectedDashboard?: LookerDashboard;
  @tracked sidebarOpen = true;

  sendMessageTask = task(
    { drop: true },
    async (request: LookerRequest): Promise<void> => {
      await waitForProperty(this, 'dashboardLoading', false);

      const iframe = document.getElementById(
        'looker-embed',
      ) as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify(request),
          ENV.lookerEmbedURL,
        );
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRoute() {
    if (this.hotelAndMarketService.market?.id) {
      return 'market.custom-reporting';
    }

    return 'destination.reporting';
  }

  @action copyDashboard(dashboard: LookerDashboard): void {
    this.dashboardOperation = {
      type: 'copy',
      dashboard,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // @ts-expect-error: Type '(item: LookerDashboard) => void' is not assignable to type '(dashboard?: LookerDashboard | undefined) => void'.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: (item: LookerDashboard) => {
        this.loadDashboard(item, false);
      },
    };
  }

  @action createDashboard(parentFolder: LookerFolder): void {
    this.dashboardOperation = {
      type: 'create',
      dashboard: this.store.createRecord<LookerDashboard>('looker-dashboard', {
        folder: parentFolder,
      }),
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // @ts-expect-error: Type '(item: LookerDashboard) => void' is not assignable to type '(dashboard?: LookerDashboard | undefined) => void'.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: (item: LookerDashboard) => {
        this.loadDashboard(item, true);
      },
    };
  }

  @action createFolder(parentFolder: LookerFolder): void {
    this.folderOperation = {
      type: 'create',
      folder: this.store.createRecord<LookerFolder>('looker-folder', {
        folder: parentFolder,
      }),
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: () => {
        this.reset();
      },
    };
  }

  @action deleteDashboard(dashboard: LookerDashboard): void {
    this.dashboardOperation = {
      type: 'delete',
      dashboard,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: () => {
        if (this.selectedDashboard?.id === dashboard.id) {
          this.selectedDashboard = undefined;
          this.router.transitionTo(`${this.baseRoute}.index`);
        }
      },
    };
  }

  @action deleteFolder(folder: LookerFolder): void {
    this.folderOperation = {
      type: 'delete',
      folder,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: () => {
        this.reset();
      },
    };
  }

  @action editDashboard(dashboard: LookerDashboard): void {
    this.dashboardOperation = {
      type: 'edit',
      dashboard,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // @ts-expect-error: Type '(item: LookerDashboard) => void' is not assignable to type '(dashboard?: LookerDashboard | undefined) => void'.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: (item: LookerDashboard) => {
        this.loadDashboard(item, false);
      },
    };
  }

  @action editFolder(folder: LookerFolder): void {
    this.folderOperation = {
      type: 'edit',
      folder,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: () => {
        this.reset();
      },
    };
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  loadDashboard(dashboard: LookerDashboard, editMode: boolean) {
    if (editMode) {
      this.triggerEdit();
      return this.router.replaceWith(
        `${this.baseRoute}.dashboard.edit`,
        dashboard.id,
      );
    }
    return this.router.transitionTo(
      `${this.baseRoute}.dashboard`,
      dashboard.id,
    );
  }

  @action moveDashboard(dashboard: LookerDashboard): void {
    this.dashboardOperation = {
      type: 'move',
      dashboard,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // @ts-expect-error: Type '(item: LookerDashboard) => void' is not assignable to type '(dashboard?: LookerDashboard | undefined) => void'.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: (item: LookerDashboard) => {
        this.loadDashboard(item, false);
      },
    };
  }

  @action moveFolder(folder: LookerFolder): void {
    this.folderOperation = {
      type: 'move',
      folder,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      close: () => {
        this.reset();
      },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      after: () => {
        this.reset();
      },
    };
  }

  @action reset(): void {
    this.folderOperation = undefined;
    this.dashboardOperation = undefined;
  }

  @action triggerDashboardFilters(filters: Record<string, unknown>): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.sendMessageTask.perform({ type: 'dashboard:filters:update', filters });
  }

  @action triggerDashboardRun(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.sendMessageTask.perform({ type: 'dashboard:run' });
  }

  @action triggerEdit(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.sendMessageTask.perform({ type: 'dashboard:edit' });
  }

  @action triggerSchedule(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.sendMessageTask.perform({ type: 'dashboard:schedule_modal:open' });
    this.pendingScheduleRequest = false;
  }
}

declare module '@ember/service' {
  interface Registry {
    'custom-reporting': CustomReportingService;
  }
}
